import { cn } from "@/lib/utils";
import { personInitials } from "@/modules/tags/tag-pill-style";

export type PersonAvatarSize = "xs" | "sm" | "md" | "lg";

/**
 * Shared person face — plan/35.
 * Neutral initials now; real `src` when we have uploads/SSO photos.
 * **No** GitHub-style colorful generated / hash avatars.
 */
export function PersonAvatar(props: {
  label: string;
  /** Real photo URL only — never a generated rainbow face. */
  src?: string | null;
  size?: PersonAvatarSize;
  /** soft = list/filter chips; solid = comments/header; accent = “me” composer */
  variant?: "soft" | "solid" | "accent";
  className?: string;
  title?: string;
}) {
  const size = props.size ?? "sm";
  const variant = props.variant ?? "soft";
  const initials = personInitials(props.label);
  const title = props.title ?? props.label;
  const src = props.src?.trim() || null;

  const sizeClass =
    size === "xs"
      ? "size-5 text-[8px]"
      : size === "sm"
        ? "size-6 text-[9px]"
        : size === "md"
          ? "size-7 text-[10px]"
          : "size-9 text-[11px]";

  const variantClass =
    variant === "soft"
      ? "border border-[var(--color-border)] bg-[var(--color-background)] text-[var(--color-foreground)]"
      : variant === "accent"
        ? "bg-[var(--color-primary)] text-[var(--color-primary-fg)]"
        : "bg-[var(--color-foreground)] text-[var(--color-card)]";

  if (src) {
    return (
      <img
        src={src}
        alt=""
        title={title}
        className={cn(
          "inline-flex shrink-0 rounded-full object-cover",
          sizeClass,
          variant === "soft" && "border border-[var(--color-border)]",
          props.className,
        )}
      />
    );
  }

  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center justify-center rounded-full font-semibold",
        sizeClass,
        variantClass,
        props.className,
      )}
      title={title}
      aria-hidden={title ? undefined : true}
    >
      {initials}
    </span>
  );
}
